import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { MaintenanceIntervalType } from '../../types/Enums';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';
import { maintenanceApi } from '../../api/maintenanceApi';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { upcomingMaintenanceItems, StoredComponent } from './upcomingMaintenanceItems';

/**
 * Represents an upcoming maintenance activity for the dashboard
 */
export interface UpcomingMaintenanceItem {
    key: string; // Unique identifier: buildingKey_componentKey_templateId
    buildingKey: string;
    buildingName: string;
    componentKey: string;
    componentLabel?: string; // Custom label if set on the component
    componentType?: string; // Component type key for translation lookup
    templateId: string;
    activityName: string; // The template's name
    nextDate: string; // YYYY-MM-DD format
    intervalType: MaintenanceIntervalType;
    intervalValue: number;
    cost?: number;
    comment?: string; // The plan row's free-text comment
    // Snooze fields (admin-only, temporary hide from active list)
    snoozedUntil?: string;  // YYYY-MM-DD format
    snoozedBy?: string;     // UID of admin who snoozed
    snoozedAt?: string;     // YYYY-MM-DD hh:mm:ss format
}

interface UseUpcomingMaintenanceReturn {
    maintenance: UpcomingMaintenanceItem[];
    snoozedMaintenance: UpcomingMaintenanceItem[];  // Snoozed items (snoozedUntil > today)
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    snoozeMaintenance: (buildingKey: string, componentKey: string, templateId: string, snoozeDate: string) => Promise<void>;
    unsnoozeMaintenance: (buildingKey: string, componentKey: string, templateId: string) => Promise<void>;
}

/**
 * Hook to fetch upcoming maintenance activities across all buildings
 * Returns activities sorted by next date (soonest first)
 * Includes overdue activities (nextDate in the past) so they can be shown as "late"
 */
export const useUpcomingMaintenance = (): UseUpcomingMaintenanceReturn => {
    const [maintenance, setMaintenance] = useState<UpcomingMaintenanceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { getTemplateById } = useBuildComponentTypes();

    const loadMaintenance = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const db = getDatabase();

            // Fetch maintenance plans and building summaries in parallel
            const [maintenanceSnapshot, buildingsSnapshot] = await Promise.all([
                get(ref(db, 'maintenance/active')),
                get(ref(db, 'buildingsSummary'))
            ]);

            if (!maintenanceSnapshot.exists()) {
                setMaintenance([]);
                return;
            }

            // Map of buildingKey -> buildingName for the buildings that exist.
            // removeBuilding archives only the building, so its maintenance plan
            // stays in maintenance/active; such plans are treated as non-existent here.
            const buildingNames = new Map<string, string | undefined>();
            if (buildingsSnapshot.exists()) {
                buildingsSnapshot.forEach((child) => {
                    buildingNames.set(child.key!, child.val()?.name);
                });
            }

            // Need to fetch component data for names (existing buildings only)
            const buildingKeys: string[] = [];
            maintenanceSnapshot.forEach((child) => {
                if (child.key && buildingNames.has(child.key)) buildingKeys.push(child.key);
            });

            // Fetch all building component data in parallel
            const componentDataPromises = buildingKeys.map(buildingKey =>
                get(ref(db, `buildings/${buildingKey}/structure/components`))
            );
            const componentSnapshots = await Promise.all(componentDataPromises);

            // Build map of buildingKey -> componentKey -> component data
            const componentsByBuilding: Record<string, Record<string, StoredComponent>> = {};
            buildingKeys.forEach((buildingKey, index) => {
                if (componentSnapshots[index].exists()) {
                    componentsByBuilding[buildingKey] = componentSnapshots[index].val();
                } else {
                    componentsByBuilding[buildingKey] = {};
                }
            });

            // Process each building's maintenance plan
            const items: UpcomingMaintenanceItem[] = [];
            maintenanceSnapshot.forEach((buildingChild) => {
                const buildingKey = buildingChild.key;
                if (!buildingKey || !buildingNames.has(buildingKey)) return;

                items.push(...upcomingMaintenanceItems(
                    buildingKey,
                    buildingNames.get(buildingKey) || 'Okänd byggnad',
                    buildingChild.val(),
                    componentsByBuilding[buildingKey] || {},
                    getTemplateById
                ));
            });

            // Sort by next date (soonest first)
            items.sort((a, b) => a.nextDate.localeCompare(b.nextDate));

            setMaintenance(items);
        } catch (err) {
            console.error('Error loading maintenance:', err);
            setError('Kunde inte ladda underhållsaktiviteter');
        } finally {
            setLoading(false);
        }
    }, [getTemplateById]);

    // Load on mount
    useEffect(() => {
        loadMaintenance();
    }, [loadMaintenance]);

    // Separate active and snoozed maintenance
    const { activeMaintenance, snoozedMaintenance } = useMemo(() => {
        const today = formatDateOnlyForStorage(new Date());

        const isSnoozed = (item: UpcomingMaintenanceItem) =>
            item.snoozedUntil && item.snoozedUntil > today;

        const active: UpcomingMaintenanceItem[] = [];
        const snoozed: UpcomingMaintenanceItem[] = [];

        for (const item of maintenance) {
            if (isSnoozed(item)) {
                snoozed.push(item);
            } else {
                active.push(item);
            }
        }

        return { activeMaintenance: active, snoozedMaintenance: snoozed };
    }, [maintenance]);

    // Snooze a maintenance activity
    const snoozeMaintenance = useCallback(async (
        buildingKey: string,
        componentKey: string,
        templateId: string,
        snoozeDate: string
    ): Promise<void> => {
        try {
            await maintenanceApi.snoozeMaintenance(buildingKey, componentKey, templateId, snoozeDate);
            await loadMaintenance();
        } catch (err: unknown) {
            console.error('Error snoozing maintenance:', err);
            throw err;
        }
    }, [loadMaintenance]);

    // Unsnooze a maintenance activity
    const unsnoozeMaintenance = useCallback(async (
        buildingKey: string,
        componentKey: string,
        templateId: string
    ): Promise<void> => {
        try {
            await maintenanceApi.unsnoozeMaintenance(buildingKey, componentKey, templateId);
            await loadMaintenance();
        } catch (err: unknown) {
            console.error('Error unsnoozing maintenance:', err);
            throw err;
        }
    }, [loadMaintenance]);

    return {
        maintenance: activeMaintenance,
        snoozedMaintenance,
        loading,
        error,
        refresh: loadMaintenance,
        snoozeMaintenance,
        unsnoozeMaintenance
    };
};
